import React, { useState, useEffect } from "react";
import {
  Building2,
  Upload,
  Users,
  Check,
  User,
  Loader2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import tokenManager from "../../utils/tokenManager";

// [Sidebar Component remains the same]
const Sidebar = ({ steps, getCurrentStepIndex }) => (
  <div className="w-80 bg-[#13161B] p-6 flex flex-col rounded-r-[20px] mb-5 mt-5">
    <div className="mb-12">
      <div className="w-20 h-8 bg-gray-700 rounded flex items-center justify-center text-sm text-gray-400">
        Logo
      </div>
    </div>

    <div className="space-y-6 flex-1">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === getCurrentStepIndex();
        const isCompleted = index < getCurrentStepIndex();

        return (
          <div key={step.id} className="flex items-start space-x-4">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                isActive
                  ? "bg-purple-600 text-white"
                  : isCompleted
                    ? "bg-green-600 text-white"
                    : "bg-[#0C0E12] text-[#94979C]"
              }`}
            >
              {isCompleted ? <Check size={20} /> : <Icon size={20} />}
            </div>
            <div className="flex-1">
              <h3
                className={`font-medium ${
                  isActive ? "text-white" : "text-[#94979C]"
                }`}
              >
                {step.title}
              </h3>
              <p className="text-sm text-[#94979C] mt-1">{step.subtitle}</p>
            </div>
          </div>
        );
      })}
    </div>

    <div className="mt-auto pt-8 text-sm text-gray-500">
      <div>© Gradeateam 2025</div>
      <div className="mt-1">help@gradeateam.co.uk</div>
    </div>
  </div>
);

// Modified Welcome Step Component - Now just collects data and moves to next step
const WelcomeStep = ({
  formData,
  setFormData,
  handleNextStep,
  handleGoogleSignUp,
  handleLogin,
  loading,
  error,
}) => (
  <div className="w-full max-w-md">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-6">
        <User size={24} className="text-gray-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-2">
        Welcome to <span className="text-[#7F56D9]">Genzi</span>
      </h1>
      <p className="text-gray-400">Let's set up your company workspace.</p>
    </div>

    {error && (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    )}

    <form className="space-y-4">
      <div>
        <label
          htmlFor="user-name"
          className="block text-sm font-medium text-gray-300 mb-2"
        >
          Name
        </label>
        <input
          id="user-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          placeholder="John Doe"
          required
          disabled={loading}
          autoComplete="name"
        />
      </div>

      <div>
        <label
          htmlFor="user-email"
          className="block text-sm font-medium text-gray-300 mb-2"
        >
          Email
        </label>
        <input
          id="user-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          placeholder="Enter your email"
          required
          disabled={loading}
          autoComplete="email"
        />
      </div>

      <div>
        <label
          htmlFor="user-password"
          className="block text-sm font-medium text-gray-300 mb-2"
        >
          Password
        </label>
        <input
          id="user-password"
          name="password"
          type="password"
          value={formData.password}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          className="w-full px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          placeholder="••••••••"
          minLength={8}
          required
          disabled={loading}
          autoComplete="new-password"
        />
      </div>

      <button
        type="button"
        onClick={handleNextStep}
        disabled={
          loading || !formData.name || !formData.email || !formData.password
        }
        className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-6 relative 
           hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
          border: "1px solid",
          borderImageSource:
            "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
          boxShadow: `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `,
          borderImageSlice: "1",
          transition: "all 0.3s ease",
        }}
      >
        Continue
      </button>

      {/* <button
        type="button"
        onClick={handleGoogleSignUp}
        disabled={loading}
        className="w-full py-3 bg-[#0C0E12] border border-[#373A41] text-white font-medium rounded-lg flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285f4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34a853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#fbbc05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#ea4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Sign up with Google
      </button> */}

      <p className="text-center text-sm text-gray-400">
        Already have an account?{" "}
        <button
          type="button"
          className="text-white hover:underline"
          onClick={handleLogin}
        >
          Log in
        </button>
      </p>
    </form>
  </div>
);

// Modified Company Step Component - Now just collects data and moves to next step
const CompanyStep = ({
  formData,
  setFormData,
  handleNextStep,
  handleGoogleSignUp,
  handleLogin,
  loading,
  error,
}) => (
  <div className="max-w-md mx-auto">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-6">
        <Building2 size={24} className="text-gray-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-2">Company details</h1>
      <p className="text-gray-400">
        These details help us configure AI tagging and insights tailored to your
        needs.
      </p>
    </div>

    {error && (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    )}

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Company name
        </label>
        <input
          type="text"
          value={formData.companyName}
          onChange={(e) =>
            setFormData({ ...formData, companyName: e.target.value })
          }
          className="w-full px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          placeholder="Genzi AI Ltd."
          required
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Estimated team size
        </label>
        <select
          value={formData.teamSize}
          onChange={(e) =>
            setFormData({ ...formData, teamSize: e.target.value })
          }
          className="w-full px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          required
          disabled={loading}
        >
          <option value="">Select team size</option>
          <option value="1-10">1-10 employees</option>
          <option value="11-50">11-50 employees</option>
          <option value="51-200">51-200 employees</option>
          <option value="201-500">201-500 employees</option>
          <option value="501+">501+ employees</option>
        </select>
      </div>

      <button
        onClick={handleNextStep}
        disabled={loading || !formData.companyName || !formData.teamSize}
        className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-6 relative 
           hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
          border: "1px solid",
          borderImageSource:
            "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
          boxShadow: `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `,
          borderImageSlice: "1",
          transition: "all 0.3s ease",
        }}
      >
        Continue
      </button>

      <button
        onClick={handleGoogleSignUp}
        disabled={loading}
        className="w-full py-3 bg-[#0C0E12] border border-[#373A41] text-white font-medium rounded-lg flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285f4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34a853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#fbbc05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#ea4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Sign up with Google
      </button>

      <p className="text-center text-sm text-gray-400">
        Already have an account?{" "}
        <button
          className="text-purple-500 hover:underline"
          onClick={handleLogin}
        >
          Log in
        </button>
      </p>
    </div>
  </div>
);

// Team Step Component remains mostly the same
const TeamStep = ({
  formData,
  setFormData,
  handleNextStep,
  setCurrentStep,
  loading,
  error,
  addEmail,
  removeEmail,
  updateEmail,
}) => (
  <div className="max-w-md mx-auto">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-6">
        <Users size={24} className="text-gray-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-2">Invite your team</h1>
      <p className="text-gray-400">
        Your new workspace has been created. Invite colleagues to collaborate.
      </p>
    </div>

    {error && (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    )}

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-4">
          Email
        </label>
        {formData.emails.map((email, index) => (
          <div key={index} className="flex gap-2 mb-3">
            <input
              type="email"
              value={email}
              onChange={(e) => updateEmail(index, e.target.value)}
              className="flex-1 px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="team-member@company.com"
              disabled={loading}
            />
            {formData.emails.length > 1 && (
              <button
                onClick={() => removeEmail(index)}
                className="p-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-400 transition-colors"
                disabled={loading}
              >
                <X size={20} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addEmail}
          className="text-purple-500 hover:underline text-sm flex items-center gap-1"
          disabled={loading}
        >
          + Add another
        </button>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setCurrentStep("assets")}
          className="flex-1 py-3 bg-[#0C0E12] border border-[#373A41] text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
          disabled={loading}
        >
          Skip for now
        </button>
        <button
          onClick={handleNextStep}
          disabled={loading}
          className="flex-1 py-3 text-white rounded-lg font-medium transition-all duration-300 relative 
             hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
            border: "1px solid",
            borderImageSource:
              "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
            boxShadow: `
              0px 36px 32px -16px #7F56D936,
              0px 8px 16px 0px #7F56D91F,
              0px 2px 4px -8px #7F56D914,
              0px 8px 24px -16px #FFFFFF3D inset,
              0px -24px 32px 0px #FFFFFF38 inset,
              0px 0px 12px 0px #FFFFFF inset
            `,
            borderImageSlice: "1",
            transition: "all 0.3s ease",
          }}
        >
          Invite and continue
        </button>
      </div>
    </div>
  </div>
);

// Modified Assets Step Component - Now performs the actual signup
const AssetsStep = ({
  formData,
  setFormData,
  handleFileUpload,
  handleCompleteSignup,
  uploadedFile,
  loading,
  error,
}) => (
  <div className="max-w-md mx-auto">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-6">
        <Upload size={24} className="text-gray-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-2">Assets details</h1>
      <p className="text-gray-400">
        These details help us configure AI tagging and insights tailored to your
        needs.
      </p>
    </div>

    {error && (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    )}

    <div className="space-y-6">
      <div>
        <input
          type="file"
          id="file-upload"
          className="hidden"
          accept="image/jpeg,image/jpg,image/png,image/svg+xml"
          onChange={handleFileUpload}
          disabled={loading}
        />
        <label
          htmlFor="file-upload"
          className={`block border-2 border-dashed border-gray-700 rounded-lg p-12 text-center hover:border-gray-600 transition-colors cursor-pointer ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {uploadedFile ? (
            <div>
              <Check size={48} className="mx-auto mb-4 text-green-500" />
              <p className="text-sm text-gray-300 mb-1">
                {uploadedFile.originalName}
              </p>
              <p className="text-xs text-gray-500">Successfully uploaded</p>
            </div>
          ) : loading ? (
            <div>
              <Loader2
                size={48}
                className="mx-auto mb-4 text-gray-400 animate-spin"
              />
              <p className="text-sm text-gray-400">Uploading...</p>
            </div>
          ) : (
            <>
              <Upload size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-sm text-gray-400 mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                SVG, PNG or JPG (max. 5MB)
              </p>
            </>
          )}
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Workspace url
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-400">genzi.ai/</span>
          <input
            type="text"
            value={formData.workspaceUrl}
            onChange={(e) => {
              // Only allow lowercase letters, numbers, and hyphens
              const value = e.target.value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, "");
              setFormData({ ...formData, workspaceUrl: value });
            }}
            className="flex-1 px-4 py-3 bg-[#0C0E12] border border-[#373A41] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            placeholder="your-workspace"
            disabled={loading}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Only lowercase letters, numbers, and hyphens allowed
        </p>
      </div>

      <button
        onClick={handleCompleteSignup}
        disabled={loading}
        className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-6 relative 
           hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
          border: "1px solid",
          borderImageSource:
            "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
          boxShadow: `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `,
          borderImageSlice: "1",
          transition: "all 0.3s ease",
        }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="animate-spin h-4 w-4" />
            Creating account...
          </span>
        ) : (
          "Complete signup"
        )}
      </button>
    </div>
  </div>
);

// [WorkspaceReady Component remains the same]
const WorkspaceReady = ({ navigate }) => (
  <div className="text-center">
    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
      <Check size={32} className="text-white" />
    </div>
    <h1 className="text-4xl font-bold text-white mb-4">
      Your workspace is ready!
    </h1>
    <p className="text-gray-400 mb-8">
      Everything is set up. You can now start using Octopush X with your team.
    </p>
    <button
      onClick={() => navigate("/overview")}
      className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-6 relative 
         hover:scale-[1.02] hover:shadow-xl"
      style={{
        background:
          "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
        border: "1px solid",
        borderImageSource:
          "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
        boxShadow: `
          0px 36px 32px -16px #7F56D936,
          0px 8px 16px 0px #7F56D91F,
          0px 2px 4px -8px #7F56D914,
          0px 8px 24px -16px #FFFFFF3D inset,
          0px -24px 32px 0px #FFFFFF38 inset,
          0px 0px 12px 0px #FFFFFF inset
        `,
        borderImageSlice: "1",
        transition: "all 0.3s ease",
      }}
    >
      Go to Dashboard
    </button>
  </div>
);

// Main OnboardingSteps Component
const OnboardingSteps = () => {
  const [currentStep, setCurrentStep] = useState("welcome");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    companyName: "",
    teamSize: "",
    workspaceUrl: "",
    emails: [""],
  });

  // Check if user is authenticated
  // useEffect(() => {
  //   const user = tokenManager.getUser();
  //   if (user && user.id) {
  //     // User is already authenticated, redirect to dashboard
  //     navigate("/dashboard");
  //   }
  // }, [navigate]);

  const handleLogin = () => {
    navigate("/login");
  };

  const handleGoogleSignUp = () => {
    window.location.href = `${
      process.env.REACT_APP_API_URL || "http://localhost:5000"
    }/api/auth/google`;
  };

  const steps = [
    {
      id: "welcome",
      title: "Your Details",
      subtitle: "Please provide your name and email.",
      icon: User,
    },
    {
      id: "company",
      title: "Company Details",
      subtitle: "Please provide your company name and size.",
      icon: Building2,
    },
    {
      id: "team",
      title: "Invite your team",
      subtitle: "Start collaborating with your team.",
      icon: Users,
    },
    {
      id: "assets",
      title: "Upload assets",
      subtitle: "Upload your company logo and set workspace URL.",
      icon: Upload,
    },
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step.id === currentStep);
  };

  const addEmail = () => {
    setFormData({
      ...formData,
      emails: [...formData.emails, ""],
    });
  };

  const removeEmail = (index) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    setFormData({ ...formData, emails: newEmails });
  };

  const updateEmail = (index, value) => {
    const newEmails = [...formData.emails];
    newEmails[index] = value;
    setFormData({ ...formData, emails: newEmails });
  };

  // Handle moving to next step without creating account
  const handleNextStep = () => {
    setError("");

    switch (currentStep) {
      case "welcome":
        // Validate welcome step data
        if (!formData.name || !formData.email || !formData.password) {
          setError("Please fill in all fields");
          return;
        }
        if (formData.password.length < 8) {
          setError("Password must be at least 8 characters");
          return;
        }
        setCurrentStep("company");
        break;

      case "company":
        // Validate company step data
        if (!formData.companyName || !formData.teamSize) {
          setError("Please fill in all fields");
          return;
        }
        setCurrentStep("team");
        break;

      case "team":
        // No validation needed, emails are optional
        setCurrentStep("assets");
        break;

      default:
        break;
    }
  };

  // 1. Enhanced handleFileUpload function with better debugging
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    console.log("File selected:", file);

    if (!file) {
      console.log("No file selected");
      return;
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/svg+xml",
    ];

    console.log("File type:", file.type);
    console.log("File size:", file.size);
    console.log("File name:", file.name);

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a valid image file (JPEG, PNG, or SVG)");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB");
      return;
    }

    setError("");

    // Store the actual File object
    const fileData = {
      file: file,
      originalName: file.name,
      size: file.size,
      type: file.type,
    };

    console.log("Setting uploadedFile:", fileData);
    setUploadedFile(fileData);
  };

  // 2. Enhanced handleCompleteSignup with better FormData debugging
  const handleCompleteSignup = async () => {
    setError("");
    setLoading(true);

    try {
      // Validate required fields first
      if (
        !formData.name ||
        !formData.email ||
        !formData.password ||
        !formData.companyName
      ) {
        throw new Error("Please fill in all required fields");
      }

      console.log("Original formData:", formData);
      console.log("uploadedFile:", uploadedFile);

      const formDataForUpload = new FormData();

      // Append all fields
      formDataForUpload.append("name", formData.name);
      formDataForUpload.append("email", formData.email);
      formDataForUpload.append("password", formData.password);
      formDataForUpload.append("companyName", formData.companyName);

      // Append optional fields
      if (formData.teamSize) {
        formDataForUpload.append("teamSize", formData.teamSize);
      }
      if (formData.workspaceUrl) {
        formDataForUpload.append("workspaceUrl", formData.workspaceUrl);
      }

      // Filter out empty emails and append non-empty ones
      const validEmails = formData.emails.filter(
        (email) => email.trim() !== "",
      );
      if (validEmails.length > 0) {
        formDataForUpload.append("emails", JSON.stringify(validEmails));
      }

      // CRITICAL: Append file correctly
      if (uploadedFile?.file) {
        console.log("Appending file to FormData:", uploadedFile.file);
        console.log("File details:", {
          name: uploadedFile.file.name,
          size: uploadedFile.file.size,
          type: uploadedFile.file.type,
          lastModified: uploadedFile.file.lastModified,
        });

        // Append the actual File object, not the wrapper
        formDataForUpload.append(
          "logo",
          uploadedFile.file,
          uploadedFile.file.name,
        );
      } else {
        console.log("No file to upload");
      }

      // Debug: Log FormData contents
      console.log("FormData contents:");
      for (let [key, value] of formDataForUpload.entries()) {
        if (value instanceof File) {
          console.log(
            `${key}:`,
            `File(${value.name}, ${value.size} bytes, ${value.type})`,
          );
        } else {
          console.log(`${key}:`, value);
        }
      }

      // Send the FormData
      const response = await apiService.register(formDataForUpload);
      console.log("Response:", response);

      if (!response.success) {
        throw new Error(response.message || "Signup failed");
      }

      // Save user data and token
      tokenManager.setUser(response.user);
      tokenManager.setTokens(response.token);

      // Success - move to ready step
      setCurrentStep("ready");
    } catch (err) {
      console.error("Signup error:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create account. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "welcome":
        return (
          <WelcomeStep
            key="welcome-step"
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
            handleGoogleSignUp={handleGoogleSignUp}
            handleLogin={handleLogin}
            loading={loading}
            error={error}
          />
        );
      case "company":
        return (
          <CompanyStep
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
            handleGoogleSignUp={handleGoogleSignUp}
            handleLogin={handleLogin}
            loading={loading}
            error={error}
          />
        );
      case "team":
        return (
          <TeamStep
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
            setCurrentStep={setCurrentStep}
            loading={loading}
            error={error}
            addEmail={addEmail}
            removeEmail={removeEmail}
            updateEmail={updateEmail}
          />
        );
      case "assets":
        return (
          <AssetsStep
            formData={formData}
            setFormData={setFormData}
            handleFileUpload={handleFileUpload}
            handleCompleteSignup={handleCompleteSignup}
            uploadedFile={uploadedFile}
            loading={loading}
            error={error}
          />
        );
      case "ready":
        return <WorkspaceReady navigate={navigate} />;
      default:
        return (
          <WelcomeStep
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
            handleGoogleSignUp={handleGoogleSignUp}
            handleLogin={handleLogin}
            loading={loading}
            error={error}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] flex">
      {currentStep !== "ready" && (
        <Sidebar steps={steps} getCurrentStepIndex={getCurrentStepIndex} />
      )}
      <div className="flex-1 flex items-center justify-center p-8">
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default OnboardingSteps;
